// The namespace, not named imports: an installed kit can be older than whole sends, and Artwork must still load.
import * as kitUi from '@flobi/asset-library/ui';
import { useState } from 'react';
import { useAppLogo } from '../lib/appDirectory';
import apiClient from '../services/apiClient';
import { useTheme } from '../theme';
import type { MenuEntry } from '../ui/menu';
import { readWholeFile, type HeldFile, type WholeFile } from './fileContents';

/*
 * Open with ▸ on a whole file: the title menu's and a Home row's. The rows are read by a hook when the flyout opens (a phone's
 * sheet too, since it is the same menu), so a menu only passing over the row reads nothing. Each row is the app's name and its
 * App Directory icon, and it runs inside the press, because the kit opens the other app's tab there.
 */

interface Row { app: string; name: string; onClick: () => unknown }
interface WholeOptions { from: string; poster: Poster; reader: Reader; onError: (message: string) => void }
type Poster = (getToken: unknown, path: string, data: unknown) => Promise<unknown>;
type Reader = (getToken: unknown, path: string) => Promise<unknown>;
interface Kit { useWholeRows?: (read: () => Promise<WholeFile>, options: WholeOptions) => { loading: boolean; rows: Row[]; problem: string | null } }

const kit = kitUi as unknown as Kit;

/** Whether the installed kit can send a whole thing. Without it the row is left out. */
export const HAS_WHOLE = typeof kit.useWholeRows === 'function';

/** The kit's calls go through Artwork's own client: Artwork never points the kit at the gateway itself. */
const poster: Poster = (_getToken, path, data) => apiClient.post(path, data).then((response) => response?.data);
const reader: Reader = (_getToken, path) => apiClient.get(path).then((response) => response?.data);

export interface WholeTarget {
  documentId: string;
  name?: string;
  /** The file, when it is the one open in the editor. */
  open?: HeldFile | null;
  onError: (message: string) => void;
}

const note = (label: string): MenuEntry[] => [{ id: 'whole-note', label, disabled: true, onSelect: () => {} }];

/**
 * The flyout's rows. The kit's first answer, before its effect has run, says "not loading, no rows": it counts as loading
 * here, or the flyout would flash "nothing can open it" for a frame.
 */
function useFileRows({ documentId, name, open, onError }: WholeTarget): readonly MenuEntry[] {
  const { theme } = useTheme();
  const logo = useAppLogo(theme === 'light' ? 'light' : 'dark');
  const state = kit.useWholeRows!(() => readWholeFile({ documentId, name, open }), { from: 'artwork', poster, reader, onError });
  const [began, setBegan] = useState(false);
  if (state.loading && !began) setBegan(true);
  if (state.loading || !began) return note('Counting…');
  if (state.problem) return note('It could not be read.');
  if (!state.rows.length) return note('Nothing can open this.');
  return state.rows.map((row) => ({ id: `whole-${row.app}`, label: row.name, logo: logo(row.app), inPress: true, onSelect: () => void row.onClick() }));
}

/** The whole file's Open with row, or nothing with a kit that can't send one. Spread it into a menu. */
export function wholeOpenWith(target: WholeTarget, label = 'Open with'): MenuEntry[] {
  if (!HAS_WHOLE) return [];
  return [{ kind: 'submenu', id: 'open-file-with', label, items: [], useItems: () => useFileRows(target) }];
}
